import { Mui } from "@osu/react-ui";
import React from "react";
import { dropdownSubtypes, inputMappings } from "../../util/enums";
import { FORM_TYPE, formTypeIdentifiers } from "../../util/constants";

export const formSections = [
  {
    id: "form",
    summary:
      "If any member of your committee has retired or left the university, for any reason, completion of the Committee and Examination Petition is required. Use the Continuing Graduate Committee Service Request option. Only one member of your committee can be so authorized.",
    questions: [
      {
        id: 'committe-exam-petition-option',
        type: inputMappings.radio,
        title: 'Please select the option that pertains to your request.',
        summary: "If the request is related to an examination, this form must be submitted to the Graduate School at least two weeks before the exam date.",
        dataField: "reasonForPetition",
        required: true,
        choices: [
          {
            id: 'one-sem-extension',
            value: "SEMESTER_EXTENSION",
            title: 'A one semester extension of the candidacy period.'
          },
          {
            id: 'waiver-of-rule',
            title: 'A waiver of the following rule in the Graduate School Handbook.',
            value: 'RULE_WAIVER',
            branch: 'grad-rule'
          },
          {
            id: 'grad-rule',
            title: 'Graduate School Handbook Waiver Rule',
            type: inputMappings.textInput,
            required: true,
            dataField: "ruleWaiverRuleId",
            dependencies: [
              'committe-exam-petition-option'
            ]
          }, 
          {
            id: 'non-member-permission',
            title: "Permission to include a non-member of the OSU graduate faculty on an exam committee. This person will be in addition to the required current Graduate Faculty members needed for a valid committee. In the Reasons section, list the committee member's name and reason for service.",
            value: "NON_OSU_GRADUATE_FACULTY",
            summary: "Requires a copy of person's CV with the petition; add attachment below. If this person is a retired or former member of the OSU graduate faculty, and was a member of your post-candidacy doctoral committee or master's committee prior to their retirement or departure,  please use the Continuing Graduate Committee Request option. (Applies only to PHD/DMA Final Defense exams and Master's exams)."
          },
          {
            id: 'continuing-grad-committee-request-option',
            title: "Continuing Graduate Committee Request option to have a former or retired member of the OSU graduate faculty continue to serve on the Ph.D. defense or master's exam committee and count towards the required faculty membership. Provide the name of the faculty member and a brief reason for committee inclusion.  If the advisor is being requested, a co-advisor with Graduate Faculty status in the student's home program should be identified.",
            value: 'FORMER_OSU_GRADUATE_FACULTY',
            branch: 'continuing-grad-committee-request-option-member-type'
          },
          {
            id: 'continuing-grad-committee-request-option-member-type',
            title: 'Member Type',
            dependencies: [
              'committe-exam-petition-option'
            ],
            type: inputMappings.radio,
            dataField: "formerCommMemberServingAs",
            summary: <React.Fragment>
              <Mui.Typography> In the Reasons section, list</Mui.Typography>
              <ol className="margin-y-1">
                <li>The retired or former faculty member's name.</li>
                <li>If request is for co-advisor status, a co-advisor must also be listed that is a current OSU Graduate Faculty member.</li>
              </ol>
              <Mui.Typography sx={{ textDecoration: "underline" }}>Only one member of your committee can be authorized under this option.</Mui.Typography>
            </React.Fragment>,
            choices: [
              {
                id: 'co-advisor',
                value: 'COADVISOR',
                title: 'Co-Advisor'
              },
              {
                id: 'committee-member',
                value: 'COMMITTEE_MEMBER',
                title: 'Committee Member'
              }
            ]
          },
          {
            id: 'other-reason',
            value: 'OTHER',
            title: 'Other (Use Reason section below; be specific).'
          }
        ]
      },
      {
        id: 'reason-for-petition',
        title: "Reason for Petition",
        type: inputMappings.textInput,
        multiline: true,
        required: true,
        dataField: "reasonForPetitionComment",
        minRows: 5,
        summary: 'Reason for Petition required for all requests. Add attachment below if necessary. Information in this request is viewable and subject to disclosure under the Ohio public records statute and rules of discovery.'
      },
      {
        id: "file-uploader",
        type: inputMappings.fileUpload,
        title: "Upload Attachments",
        Description: () => (
          <Mui.Typography variant="body2" className="margin-bottom-1">
            (Supported file types: Text, PDF, Microsoft Word, Microsoft Excel, and images with extensions bmp, gif, jpeg, png and tiff)
          </Mui.Typography>
        ),
        dataField: "attachments",
        required: false,
        acceptedFileTypes: ["application/msword", ".doc", ".docx", "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          ".xls", ".xlsx", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "image/gif", ".gif",
          "image/png", ".png", "image/jpeg", ".jpeg", "image/bmp", ".bmp", "image/tiff", ".tiff", "text/plain", "application/pdf", ".txt"
        ],
        maxFileCount: 5,
        maxFileSize: 20000000 // 20MB
      },
      {
        title: "Graduation Level",
        id: "graduation-information-level",
        required: true,
        type: "dropDown",
        dataField: "gradLevel",
        summary:
          "If you are uncertain of your academic level, please contact your graduate program coordinator.",
        subtype: dropdownSubtypes.graduationLevel,
        branch: "graduation-information-plan",
      },
      {
        title: "Graduation Plan",
        id: "graduation-information-plan",
        dataField: "academicPlan",
        required: true,
        type: "dropDown",
        subtype: dropdownSubtypes.graduatePrograms,
        filter: [
          {
            field: "graduation-information-level",
            match: "gradLevel",
            operation: "includes"
          },
        ],
        results: [
          {
            label: "Graduation Degree",
            field: "degree",
            dataField: "gradDegree",
          },
        ],
        branch: 'advisor-info',
        dependencyHelperText: "Graduation Level must be selected before choosing a Graduation Plan",
        dependencies: ["graduation-information-level"],
      },
      {
        id: "advisor-info",
        type: "dropDown",
        title: "Advisor",
        subtype: dropdownSubtypes.advisor,
        dataField: "advisor",
        dependencies: [
          "graduation-information-plan"
        ],
        dependencyHelperText: "Graduation Plan must be selected before choosing an Advisor",
        required: true
      },
    ],
  },
];

export const additionalFilters = [ {
  id: "submitted-date-after",
  title: "Submitted Date After",
  type: inputMappings.date,
  startTime: false,
  endTime: false,
},
{
  id: "submitted-date-before",
  title: "Submitted Date Before",
  type: inputMappings.date,
  startTime: false,
  endTime: false,
},
{
  id: "exam-reason",
  title: "Committee and Examination Reason",
  required: false,
  type: inputMappings.dropDown,
  subtype: dropdownSubtypes.examReason
}];
